import { useState, useRef, useCallback, useEffect } from "react";
import { DataGridUrlStateManager } from "../DataGridUrlStateManager";

interface UseDebouncedSearchOptions {
  urlStateManager: DataGridUrlStateManager;
  apiRef: React.RefObject<any>;
  debounceMs?: number;
}

interface UseDebouncedSearchReturn {
  searchValue: string;
  searchQueryRef: React.MutableRefObject<string>;
  handleSearchChange: (value: string) => void;
}

/**
 * Hook for managing debounced search state with URL synchronization.
 * Handles search input, debouncing, URL state persistence, and triggering data fetches.
 */
export function useDebouncedSearch({
  urlStateManager,
  apiRef,
  debounceMs = 1000,
}: UseDebouncedSearchOptions): UseDebouncedSearchReturn {
  // Initialize search value from URL to avoid double-fetch on mount
  const [searchValue, setSearchValue] = useState(() => {
    return urlStateManager.readSearchFromUrl() || "";
  });

  // Ref to store the current search query for API calls (avoids re-creating dataSource)
  const searchQueryRef = useRef(searchValue);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      searchDebounceRef.current = setTimeout(() => {
        // Update the ref that getRows reads from
        searchQueryRef.current = value;
        urlStateManager.writeSearchToUrl(value);

        if (apiRef.current) {
          apiRef.current.dataSource.fetchRows();
        }
      }, debounceMs);
    },
    [urlStateManager, apiRef, debounceMs]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  return {
    searchValue,
    searchQueryRef,
    handleSearchChange,
  };
}
